import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import ReadOnlyField from './readonly-field';

interface SelectFieldProps<TFieldValues extends FieldValues> {
  /**
   * Field name in the form
   */
  readonly name: Path<TFieldValues>;

  /**
   * Form control from react-hook-form
   */
  readonly control: Control<TFieldValues>;

  /**
   * Label for the select field
   */
  readonly label: string;

  /**
   * Options to display in the dropdown
   */
  readonly options: Array<{ value: string | number; label: string }>;

  /**
   * Whether the field is in read-only mode
   */
  readonly isReadOnly?: boolean;

  /**
   * Whether the field is required
   */
  readonly required?: boolean;

  /**
   * Whether the field is disabled (in edit mode)
   */
  readonly disabled?: boolean;
}

/**
 * A reusable select field component that can be displayed in both
 * edit mode (dropdown selector) and read-only mode (text display).
 */
const SelectField = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  options,
  isReadOnly = false,
  required = false,
  disabled = false,
}: SelectFieldProps<TFieldValues>) => {
  // Helper function to get display label from value
  const getLabelFromValue = (value: string | number) => {
    const option = options.find(opt => opt.value === value);
    return option ? option.label : value.toString();
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        if (isReadOnly) {
          return (
            <ReadOnlyField
              label={label}
              value={field.value ? getLabelFromValue(field.value) : ''}
            />
          );
        }

        return (
          <FormControl fullWidth error={!!fieldState.error} required={required}>
            <InputLabel id={`${String(name)}-select-label`}>{label}</InputLabel>
            <Select
              {...field}
              labelId={`${String(name)}-select-label`}
              label={label}
              value={field.value ?? ''}
              error={!!fieldState.error}
              disabled={disabled}
            >
              {options.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};

export default SelectField;
